import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'qapp.travel.timelineVisibility';
const EVENT_NAME = 'qapp-travel-timeline-visibility-changed';

export const PLACE_MODES = ['first', 'last', 'all', 'none'];

const DEFAULTS = {
  placeMode: 'all',
  showStays: true,
  showTransports: true,
};

function normalize(raw) {
  if (!raw || typeof raw !== 'object') return { ...DEFAULTS };
  const placeMode = PLACE_MODES.includes(raw.placeMode) ? raw.placeMode : DEFAULTS.placeMode;
  return {
    placeMode,
    showStays: typeof raw.showStays === 'boolean' ? raw.showStays : DEFAULTS.showStays,
    showTransports:
      typeof raw.showTransports === 'boolean' ? raw.showTransports : DEFAULTS.showTransports,
  };
}

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) return { ...DEFAULTS };
    return normalize(JSON.parse(raw));
  } catch {
    return { ...DEFAULTS };
  }
}

function writeStored(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Timeline day-layer visibility (places mode + stays/transports toggles).
 * Persists in localStorage; syncs via window event + storage.
 */
export default function useTravelTimelineVisibility() {
  const [visibility, setVisibilityState] = useState(() => readStored());

  useEffect(() => {
    const onCustom = (e) => {
      if (e.detail) setVisibilityState(normalize(e.detail));
    };
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY || e.newValue == null) return;
      try {
        setVisibilityState(normalize(JSON.parse(e.newValue)));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener(EVENT_NAME, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setVisibility = useCallback((updater) => {
    setVisibilityState((prev) => {
      const next = normalize(typeof updater === 'function' ? updater(prev) : updater);
      writeStored(next);
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
      return next;
    });
  }, []);

  const patchVisibility = useCallback(
    (partial) => {
      setVisibility((prev) => ({ ...prev, ...partial }));
    },
    [setVisibility]
  );

  const isDefault =
    visibility.placeMode === DEFAULTS.placeMode &&
    visibility.showStays === DEFAULTS.showStays &&
    visibility.showTransports === DEFAULTS.showTransports;

  /** EyeOff only when every day layer is off. */
  const allLayersHidden =
    visibility.placeMode === 'none' &&
    !visibility.showStays &&
    !visibility.showTransports;

  return { visibility, setVisibility, patchVisibility, isDefault, allLayersHidden, defaults: DEFAULTS };
}
